import { defaultFeaturedBoxesCopy } from "@loot-os/api-contract";
import type { FeaturedBoxesContent } from "@loot-os/design-system/templates/home/homeContent";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useCurrency } from "../../../../lib/currency.js";
import { boxesQueryOptions } from "../../../../lib/queries.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { useBoxCards } from "../../shared/boxCards.js";

/** How many boxes the row shows when the store featured none: two rows of the home grid. */
export const UNFEATURED_ROW_LIMIT = 10;

/**
 * The home page's box row: the boxes marked `featured`, out of everything GET /v1/boxes returns,
 * or the first `UNFEATURED_ROW_LIMIT` boxes when the store featured none. Either way it links to
 * the whole catalog, and it renders nothing only for a store with no boxes at all.
 */
export function useFeaturedBoxesContent(): FeaturedBoxesContent {
  const currency = useCurrency();
  const terms = useTerminology();
  const copy = defaultFeaturedBoxesCopy(terms);
  const query = useQuery(boxesQueryOptions());
  const row = useMemo(() => {
    if (!query.data) return null;
    const featured = query.data.filter((b) => b.featured);
    return featured.length > 0 ? featured : query.data.slice(0, UNFEATURED_ROW_LIMIT);
  }, [query.data]);
  const cards = useBoxCards(row);
  const error = query.error
    ? query.error instanceof Error
      ? query.error.message
      : `failed to load ${terms.box.plural}`
    : null;

  return {
    title: copy.title,
    currency,
    cards,
    errorMessage: error ? error : null,
    hidden: cards?.length === 0 && !error,
    viewAll: { label: copy.viewAll, href: "/boxes" },
  };
}
